<template>
    <div :id="`canvas${prefixId}`" style="width: 100%; height: 100%"></div>
</template>
<script>
import { markRaw, toRaw } from "vue";
import * as THREE from "three";
import { DragControls } from "three/examples/jsm/controls/DragControls";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Text } from "troika-three-text";
import addBarrier from "../../utils/addBarrier";
import addStripe from "../../utils/addStripe";
import addBorder from "../../utils/addBorder";
import addPaving from "../../utils/addPaving";
import addFacingTop from "../../utils/addFacingTop";
import addFacingTopStripe from "../../utils/addFacingTopStripe";
import addFacingSide from "../../utils/addFacingSide";
import { InteractionManager } from "three.interactive";

import getPosInfo from "../../utils/getPosInfo";
import modelSetY from "../../utils/modelSetY";
import getGLTF from "../../utils/getGLTF";

import { prepareEngraving } from "../../utils/prepareEngraving";
import modelToMesh from "../../utils/modelToMesh";
import getMaterialTexture from "../../utils/getMaterialTexture";

import addOutline from "../../utils/addOutline";
import addArrowRotate from "../../utils/addArrowRotate";
import { getAngleNumber } from "../../utils/calcAngle";

export default {
    name: "ThreeCanvas",
    props: {
        prefixId: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            // three.js и связанные объекты помечаем как raw,
            // чтобы Vue 3 не оборачивал их во Proxy и не ломал внутренние поля
            renderer: null,
            scene: null,
            camera: null,
            cubeTextureLoader: null,
            environmentMap: null,
            ambientLight: null,
            directionalLight: null,
            fillLight: null,
            pointLight: null,
            sizes: null,
            controls: null,
            transformControls: null,
            axesHelper: null,
            textureLoader: null,
            plotTexture: null,
            elementsGroup: null,
            landscapingGroup: null,
            borderGroup: null,
            outlineGroup: null,
            arrowRotateGroup: null,
            engravingGroup: null,
            engravingGroups: markRaw({}),
            graveGroup: null,
            barrierGroup: null,
            plotHeight: 0.01,

            raycaster: null,
            mousePosition: null,
            plot3D: null,
            pseudoEl3D: null,
            barrier3D: null,
            elements3D: markRaw({}),
            landscaping3D: markRaw({}),

            dragControls: null,
            objectsMoving: [],
            movingY: 0,
            movingType: "element",
            activable: null,
            activeUuid: null,
            helper: null,
            canvas: null,
            interactionManager: null,
            animationFrameId: null, // ID для отмены анимационного цикла
            sceneMousedownHandler: null, // Обработчик события mousedown на сцене
        };
    },

    methods: {
        doubleClick: function () {
            if (window.getSelection) {
                window.getSelection().removeAllRanges();
            } else {
                document.selection.empty();
            }
            return false;
        },
        getBBox(obj) {
            return new THREE.Box3(
                new THREE.Vector3(),
                new THREE.Vector3()
            ).setFromObject(obj);
        },
        checkUp() {
            const objModels = [];

            if (this.barrier3D) {
                this.barrier3D.position.y = 0;
                this.barrier3D.updateMatrix();

                if (this.landscaping3D.mainElement) {
                    const bboxBarrierElement = this.getBBox(this.barrier3D);
                    const bboxMainElementLandscaping = this.getBBox(
                        this.landscaping3D.mainElement
                    );

                    let isUpBarrier = true;

                    if (
                        this.landscaping.mainElement.element_group_slug ==
                            "plate-ready" ||
                        (this.landscaping.mainElement.element_group_slug ==
                            "stripe" &&
                            this.landscaping.mainElement
                                .individual_size_material == 1) ||
                        (this.landscaping.mainElement.element_group_slug ==
                            "plate-fill" &&
                            this.landscaping.mainElement
                                .individual_size_material == 1)
                    ) {
                        isUpBarrier = false;
                    }

                    if (isUpBarrier) {
                        this.barrier3D.position.y =
                            bboxMainElementLandscaping.max.y;
                        this.barrier3D.updateMatrix();
                    }
                }
            }

            this.scene.traverse((node) => {
                if (node.isCheckup && node.isCheckup === true) {
                    let orderElement = null;

                    switch (node.nameType) {
                        case "element":
                            orderElement = this.getOrderElementByUuid(
                                node.uuid
                            );
                            break;
                        case "barrier":
                            orderElement = this.barrier;
                            break;
                        case "mainLandscaping":
                            orderElement = this.landscaping.mainElement;
                            break;
                        case "borderLandscaping":
                            orderElement = this.landscaping.borderElement;
                            break;
                        // case 'fillLandscaping':
                        //     orderElement = this.landscaping.fillElement;
                        //     break;
                        default:
                            break;
                    }

                    let nodeBB = this.getBBox(node);
                    if (orderElement && node.nameType === "element") {
                        node.position.y = 0 + (nodeBB.max.y - nodeBB.min.y) / 2;
                        node.updateMatrix();
                    }
                    nodeBB = this.getBBox(node);

                    if (objModels.length) {
                        objModels.forEach((obj) => {
                            if (nodeBB.intersectsBox(obj.bb)) {
                                //   console.log("Объекты пересеклись");

                                //здесь учавствуют все компоненты сцены, тут нужно будет определять что будет выталкиваться наверх, по методу "всплывающего пузырька")
                                if (node.nameType === "element") {
                                    if (
                                        orderElement &&
                                        +orderElement.level > obj.level &&
                                        !obj.not_install_up
                                    ) {
                                        let addHeight =
                                            (nodeBB.max.y - nodeBB.min.y) / 2;
                                        node.position.y =
                                            obj.bb.max.y + addHeight;

                                        node.updateMatrix();
                                        nodeBB = this.getBBox(node);
                                    }

                                    if (
                                        orderElement &&
                                        +orderElement.level < obj.level &&
                                        !+orderElement.not_install_up
                                    ) {
                                        if (obj.model.nameType === "element") {
                                            let addHeight =
                                                (obj.bb.max.y - obj.bb.min.y) /
                                                2;
                                            obj.model.position.y =
                                                nodeBB.max.y + addHeight;
                                            obj.model.updateMatrix();
                                        }
                                    }
                                }
                            } else {
                                //   console.log("Объекты НЕ пересеклись");
                            }
                        });
                    }

                    if (orderElement) {
                        objModels.push({
                            model: node,
                            bb: nodeBB,
                            level: orderElement.level ? +orderElement.level : 0,
                            not_install_up: orderElement.not_install_up
                                ? +orderElement.not_install_up
                                : 0,
                        });
                    }
                }
            });
        },
        sceneChanged() {
            // Проверяем, что компонент инициализирован
            if (!this.scene) return;
            this.checkUp();
        },

        init: function () {
            this.canvas = document.getElementById(`canvas${this.prefixId}`);
            this.cubeTextureLoader = markRaw(new THREE.CubeTextureLoader());
            this.textureLoader = markRaw(new THREE.TextureLoader());

            this.raycaster = markRaw(new THREE.Raycaster());
            this.mousePosition = markRaw(new THREE.Vector2());

            this.scene = markRaw(new THREE.Scene());

            this.elementsGroup = markRaw(new THREE.Group());
            this.landscapingGroup = markRaw(new THREE.Group());
            this.borderGroup = markRaw(new THREE.Group());
            this.graveGroup = markRaw(new THREE.Group());
            this.barrierGroup = markRaw(new THREE.Group());
            this.arrowRotateGroup = markRaw(new THREE.Group());

            this.outlineGroup = markRaw(new THREE.Group());
            this.engravingGroup = markRaw(new THREE.Group());

            this.scene.add(
                this.elementsGroup,
                this.landscapingGroup,
                this.graveGroup,
                this.borderGroup,
                this.barrierGroup,
                this.arrowRotateGroup,
                this.outlineGroup,
                this.engravingGroup
            );

            let envSize = 20;
            if (this.settings.size_3d_environment) {
                envSize = this.settings.size_3d_environment;
            }

            // чуть более тёмный фон для усиления контраста с объектами
            this.scene.background = new THREE.Color(0x222222);
            this.scene.environment = this.scene.background;

            if (
                parseInt(this.settings.env_3d_texture) != 1 &&
                this.noShowSceneEnv !== 1
            ) {
                let envImages = [
                    "/textures/environmentMaps/7/px.png",
                    "/textures/environmentMaps/7/nx.png",
                    "/textures/environmentMaps/7/py.png",
                    "/textures/environmentMaps/7/ny.png",
                    "/textures/environmentMaps/7/pz.png",
                    "/textures/environmentMaps/7/nz.png",
                ];

                if (parseInt(this.settings.env_3d_texture) == 2) {
                    envImages = [
                        this.settingImages.env_px,
                        this.settingImages.env_nx,
                        this.settingImages.env_py,
                        this.settingImages.env_ny,
                        this.settingImages.env_pz,
                        this.settingImages.env_nz,
                    ];
                }
                this.environmentMap = this.cubeTextureLoader.load(envImages);
                this.environmentMap.colorSpace = THREE.SRGBColorSpace;

                this.scene.environment = this.environmentMap;

                const cubeEnvGeo = new THREE.BoxGeometry(
                    envSize,
                    envSize,
                    envSize
                );
                const cubeEnvMat = envImages.map((img) => {
                    let texture = this.textureLoader.load(img);
                    return new THREE.MeshBasicMaterial({
                        map: texture,
                        side: THREE.BackSide,
                    });
                });
                const cubeEnvironment = new THREE.Mesh(cubeEnvGeo, cubeEnvMat);
                cubeEnvironment.position.y =
                    envSize / 2 - this.plotHeight / 2 - 0.0005;
                cubeEnvironment.name = "cubeEnvironment";
                this.scene.add(cubeEnvironment);

                this.canvas.addEventListener("dblclick", this.doubleClick);
            }

            // Направленный свет — как на странице гравировки для естественного освещения
            // чуть более мягкий свет, чтобы убрать пересвеченность
            this.directionalLight = markRaw(
                new THREE.DirectionalLight("#ffffff", 2.2)
            );
            this.directionalLight.position.set(0, 2, 1);
            this.scene.add(this.directionalLight);

            // Удаляем старые источники света если они есть
            if (this.fillLight) {
                this.scene.remove(this.fillLight);
                this.fillLight = null;
            }
            if (this.ambientLight) {
                this.scene.remove(this.ambientLight);
                this.ambientLight = null;
            }

            // Дополнительные источники света для улучшения освещения:
            // Ambient light для заполнения теней и уменьшения контраста
            this.ambientLight = markRaw(new THREE.AmbientLight(0xffffff, 0.3));
            this.scene.add(this.ambientLight);

            // Fill light (заполняющий свет) с противоположной стороны от основного света
            // для смягчения теней и более равномерного освещения
            this.fillLight = markRaw(new THREE.DirectionalLight(0xffffff, 0.5));
            this.fillLight.position.set(0, 1, -1);
            this.scene.add(this.fillLight);

            this.sizes = {
                width: this.canvas.clientWidth,
                height: this.canvas.clientHeight,
            };

            this.camera = markRaw(
                new THREE.PerspectiveCamera(
                    65,
                    this.sizes.width / this.sizes.height,
                    0.1,
                    5000
                )
            );

            this.controls = markRaw(
                new OrbitControls(this.camera, this.canvas)
            );
            this.controls.enableDamping = true;

            //PAN  движение сцены правой кнопкой мыши

            // this.controls.enablePan = false;

            this.camera.position.set(1.5, 1.5, 3);
            // this.axesHelper = new THREE.AxesHelper(5);
            // this.scene.add(this.axesHelper);
            this.controls.minPolarAngle = -Math.PI;
            this.controls.maxPolarAngle = Math.PI / 2.02;
            this.controls.minDistance = 1;
            this.controls.maxDistance = 7;

            //Для снимка сцены
            // this.camera.position.set(1.2, 1.4, 1.8);
            // this.controls.enabled = false;

            this.scene.add(this.camera);

            this.renderer = markRaw(
                new THREE.WebGLRenderer({
                    antialias: this.lowPerformance ? false : true,
                    preserveDrawingBuffer: true,
                    powerPreference: "high-performance",
                })
            );

            if (this.lowPerformance) {
                this.renderer.setPixelRatio(1);
            } else {
                this.renderer.shadowMap.enabled = true;
                this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
                this.renderer.setPixelRatio(
                    Math.min(window.devicePixelRatio, 2)
                );
            }

            // three.js r155+ использует физически корректное освещение по умолчанию.
            // Свойство physicallyCorrectLights удалено, используем useLegacyLights
            // для явного указания режима освещения.
            this.renderer.useLegacyLights = false;

            // Настройка цветового пространства для корректного отображения текстур
            this.renderer.outputColorSpace = THREE.SRGBColorSpace;

            // ACES Filmic tone mapping для более естественной картинки (как в эталоне ThreeSceneEngraving)
            // и устранения пересвеченности / синеватого оттенка
            this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
            this.renderer.toneMappingExposure = 1.0;

            this.renderer.setSize(this.sizes.width, this.sizes.height);
            this.canvas.appendChild(this.renderer.domElement);

            this.interactionManager = markRaw(
                new InteractionManager(
                    this.renderer,
                    this.camera,
                    this.renderer.domElement
                )
            );

            this.interactionManager.add(this.scene);
        },

        animate: function () {
            if (!this.renderer || !this.scene || !this.camera) {
                return; // Защита от вызова после dispose
            }
            this.controls.update();
            this.interactionManager.update();
            this.animationFrameId = requestAnimationFrame(this.animate);
            this.renderer.render(this.scene, this.camera);
        },

        async createMockup() {
            const mockup = await this.renderer.domElement.toDataURL(
                "image/jpeg"
            );
            this.$store.dispatch("clientOrder/setMockup", mockup);
        },

        getOrderElementByUuid(uuid) {
            let orderElement = null;
            if (
                this.landscaping.mainElement &&
                this.landscaping.mainElement.meshId &&
                this.landscaping.mainElement.meshId == uuid
            ) {
                orderElement = this.landscaping.mainElement;
                // исправляем опечатку – нужно "landscaping"
                this.movingType = "landscaping";
            }

            if (!orderElement) {
                orderElement = this.elements.find((el) => el.meshId == uuid);
            }

            if (orderElement) {
                this.movingY = orderElement.position.y;
            }

            return orderElement;
        },

        removeEngraving(uuid) {
            if (!this.engravingGroups[uuid]) return false;

            const group = this.engravingGroups[uuid];

            // Собираем объекты для очистки (traverse изменяет коллекцию)
            const objectsToDispose = [];
            group.traverse((child) => {
                if (child !== group) {
                    objectsToDispose.push(child);
                }
            });

            // Освобождаем ресурсы
            objectsToDispose.forEach((child) => {
                // Troika Text
                if (child.isText && typeof child.dispose === "function") {
                    child.dispose();
                }

                // Геометрия
                if (
                    child.geometry &&
                    typeof child.geometry.dispose === "function"
                ) {
                    child.geometry.dispose();
                }

                // Материалы
                if (child.material) {
                    this.disposeMaterial(child.material);
                }
            });

            // Очищаем и удаляем группу
            group.clear();

            if (group.parent) {
                group.parent.remove(group);
            }

            delete this.engravingGroups[uuid];

            return true;
        },

        /**
         * Освобождает материал и все его текстуры
         */
        disposeMaterial(material) {
            if (!material) return;

            // Обработка массива материалов
            if (Array.isArray(material)) {
                material.forEach((mat) => this.disposeMaterial(mat));
                return;
            }

            // Список возможных текстурных свойств
            const textureProps = [
                "map",
                "alphaMap",
                "aoMap",
                "bumpMap",
                "displacementMap",
                "emissiveMap",
                "envMap",
                "lightMap",
                "metalnessMap",
                "normalMap",
                "roughnessMap",
                "specularMap",
                "gradientMap",
            ];

            textureProps.forEach((prop) => {
                const texture = material[prop];
                if (texture && typeof texture.dispose === "function") {
                    texture.dispose();
                }
            });

            // Освобождаем сам материал
            if (typeof material.dispose === "function") {
                material.dispose();
            }
        },

        removeOutline() {
            if (this.outlineGroup) {
                this.outlineGroup.clear();
            }
        },

        addOutline(model) {
            this.removeOutline();
            if (model) {
                const outline = addOutline(model);
                if (outline) {
                    this.outlineGroup.add(outline);
                }
            }
        },

        removeArrowRotate() {
            if (!this.arrowRotateGroup) return;
            
            if (this.arrowRotateGroup.children.length && this.interactionManager) {
                this.arrowRotateGroup.children.forEach((element) => {
                    this.interactionManager.remove(element);
                });
            }
            this.arrowRotateGroup.clear();
        },

        async addArrowRotate(model) {
            this.removeArrowRotate();
            const [leftArrow, rightArrow] = await addArrowRotate(model);
            if (leftArrow) {
                this.arrowRotateGroup.add(leftArrow);
                this.interactionManager.add(leftArrow);
                leftArrow.addEventListener("mouseover", this.handleMouseOver);
                leftArrow.addEventListener("mouseout", this.handleMouseOut);
                leftArrow.addEventListener(
                    "mousedown",
                    this.handleLeftArrowMousedown
                );
            }
            if (rightArrow) {
                this.arrowRotateGroup.add(rightArrow);
                this.interactionManager.add(rightArrow);
                rightArrow.addEventListener("mouseover", this.handleMouseOver);
                rightArrow.addEventListener("mouseout", this.handleMouseOut);
                rightArrow.addEventListener(
                    "mousedown",
                    this.handleRightArrowMousedown
                );
            }
        },

        handleMouseOver(event) {
            document.body.style.cursor = "pointer";
            event.target.scale.set(1.1, 1.1, 1.1);
        },
        handleMouseOut(event) {
            document.body.style.cursor = "default";
            event.target.scale.set(1, 1, 1);
        },

        handleLeftArrowMousedown(event) {
            this.removeOutline();
            this.activable.rotation.y += this.rotateAngle;
            //   this.engravingGroups[this.activable.uuid] += this.rotateAngle;
            this.activable.updateMatrix();
            this.addOutline(this.activable);
            this.saveActivableRotation();
            this.addArrowRotate(this.activable);
        },
        handleRightArrowMousedown(event) {
            this.removeOutline();
            this.activable.rotation.y -= this.rotateAngle;
            //   this.engravingGroups[this.activable.uuid] -= this.rotateAngle;
            this.activable.updateMatrix();
            this.addOutline(this.activable);
            this.saveActivableRotation();
            this.addArrowRotate(this.activable);
        },

        saveActivableRotation() {
            if (this.movingType == "landscaping") {
                this.$store.dispatch(
                    "clientOrder/setRotateLandscapingMainElement",
                    {
                        meshId: this.activable.uuid,
                        rotate: this.activable.rotation.y,
                    }
                );
            } else {
                this.$store.dispatch("clientOrder/setRotateElement", {
                    meshId: this.activable.uuid,
                    rotate: this.activable.rotation.y,
                });
            }
            this.sceneChanged();
        },

        clearActivable: async function () {
            this.removeOutline();
            this.removeArrowRotate();
        },

        setActivable: async function (model) {
            const orderElement = this.getOrderElementByUuid(model.uuid);
            if (!orderElement) return false;
            this.activable = model;
            this.addOutline(model);
            if (orderElement.rotating) {
                this.addArrowRotate(model);
            }
        },

        dragStart: async function (event) {
            const orderElement = this.getOrderElementByUuid(event.object.uuid);
            if (!orderElement || orderElement.moving == "no") return false;

            await this.setActivable(event.object);

            this.controls.enabled = false;
            event.object.material.opacity = 0.33;

            // Делаем гравировку полупрозрачной
            if (this.engravingGroups[event.object.uuid]) {
                this.engravingGroups[event.object.uuid].traverse((child) => {
                    if (child.material) {
                        child.material.opacity = 0.33;
                        child.material.transparent = true;
                    }
                });
            }
        },

        drag: async function (event) {
            event.object.position.y = this.movingY;
            event.object.updateMatrix();
            this.removeOutline();
            this.removeArrowRotate();
            // Гравировка автоматически следует за моделью как дочерний элемент
        },

        dragEnd: async function (event) {
            const orderElement = this.getOrderElementByUuid(event.object.uuid);
            if (!orderElement) return false;

            event.object.material.opacity = 1;
            this.controls.enabled = true;

            // Возвращаем непрозрачность гравировке
            if (this.engravingGroups[event.object.uuid]) {
                this.engravingGroups[event.object.uuid].traverse((child) => {
                    if (child.material) {
                        child.material.opacity = 1;
                    }
                });
            }

            if (this.movingType == "landscaping") {
                this.$store.dispatch(
                    "clientOrder/setPositionLandscapingMainElement",
                    {
                        meshId: event.object.uuid,
                        position: event.object.position,
                    }
                );
            } else {
                this.$store.dispatch("clientOrder/setPositionElement", {
                    meshId: event.object.uuid,
                    position: event.object.position,
                });
            }

            event.object.updateMatrix();
            this.movingY = 0;
            this.movingType = "element";
            this.addOutline(event.object);

            // Гравировка уже привязана к модели — создаем только если отсутствует
            if (
                !this.engravingGroups[event.object.uuid] &&
                orderElement.faces?.length
            ) {
                await this.addEngraving(event.object, orderElement);
            }

            if (orderElement.rotating) {
                this.addArrowRotate(event.object);
            }
            this.sceneChanged();
        },

        disposeDragControls: function () {
            if (this.dragControls) {
                this.dragControls.removeEventListener("drag", this.drag);
                this.dragControls.removeEventListener(
                    "dragstart",
                    this.dragStart
                );
                this.dragControls.removeEventListener("dragend", this.dragEnd);
                this.dragControls.dispose();
                this.dragControls = null;
            }
        },

        initDragControls: async function () {
            // Удаляем старый контроллер перед созданием нового
            this.disposeDragControls();

            const draggable = this.objectsMoving
                .map((obj) => toRaw(obj))
                .filter((obj) => obj && obj.isObject3D && obj.parent);

            if (!draggable.length || !this.camera || !this.renderer) {
                return;
            }

            this.dragControls = markRaw(
                new DragControls(
                    [...draggable],
                    this.camera,
                    this.renderer.domElement
                )
            );

            this.dragControls.addEventListener("drag", this.drag);
            this.dragControls.addEventListener("dragstart", this.dragStart);
            this.dragControls.addEventListener("dragend", this.dragEnd);
        },

        async addEngraving(model, el) {
            // Безопасно удаляем существующую гравировку если есть
            if (this.engravingGroups[model.uuid]) {
                this.removeEngraving(model.uuid);
            }

            const elFaces = this.faces.filter(
                (f) => f.element_id == el.element_id
            );

            model.updateMatrix();

            let engravingMeshes = [];

            if (elFaces.length) {
                const loader = new THREE.TextureLoader();
                for (const face of elFaces) {
                    if (face.elements.length) {
                        for (const fEl of face.elements) {
                            const elImage = fEl.imageOriginal || fEl.image;

                            const texture = loader.load(elImage);

                            var material = new THREE.MeshStandardMaterial({
                                map: texture,
                                alphaMap: texture,
                                alphaTest: 0.01,
                                transparent: true,
                                //   side: THREE.DoubleSide,
                                //   color: 0xffffff,
                            });

                            let elWidth = fEl.width / 100;
                            let elHeight = fEl.height / 100;

                            var geometry = new THREE.BoxGeometry(
                                elWidth,
                                elHeight,
                                0.0001
                            );

                            // FIX: Используем face.face_id вместо face.id для поиска distance
                            // face.face_id - это корректный идентификатор из Vuex store
                            // face.id - это legacy идентификатор, который может отсутствовать
                            const distance = el.size.face_distances.find(
                                (fd) =>
                                    fd.face_id == face.face_id ||
                                    fd.face_id == face.id
                            );

                            // Создаем mesh и сразу оборачиваем в markRaw,
                            // чтобы Vue 3 не оборачивал его в Proxy
                            const mesh = markRaw(
                                await prepareEngraving(
                                    fEl,
                                    model,
                                    new THREE.Mesh(geometry, material),
                                    face,
                                    distance
                                )
                            );
                            engravingMeshes.push(mesh);
                        }
                    }
                    if (face.texts.length) {
                        for (const text of face.texts) {
                            // Обертываем Text в markRaw() сразу после создания,
                            // чтобы Vue 3 не оборачивал его в Proxy и не ломал
                            // доступ к внутренним свойствам (например, baseMaterial)
                            let myText = markRaw(new Text());

                            myText.text = text.text;
                            myText.fontSize =
                                parseFloat(text.size.height) / 100;
                            myText.font = text.font.file;
                            myText.color = 0xffffff;
                            if (text.align && text.align.id) {
                                myText.textAlign = text.align.id;
                            }

                            // FIX: Дожидаемся завершения sync() перед использованием текста
                            // sync() — асинхронная операция, которая генерирует геометрию текста
                            await new Promise((resolve) => {
                                myText.sync(() => resolve());
                            });

                            // FIX: Используем face.face_id вместо face.id для поиска distance
                            const distance = el.size.face_distances.find(
                                (fd) =>
                                    fd.face_id == face.face_id ||
                                    fd.face_id == face.id
                            );

                            myText = await prepareEngraving(
                                text,
                                model,
                                myText,
                                face,
                                distance
                            );

                            // Всегда оборачиваем результат в markRaw, чтобы Vue 3
                            // не оборачивал Three.js объекты в Proxy
                            // Это критично для troika-three-text объектов, которые
                            // имеют read-only свойства (например, baseMaterial)
                            engravingMeshes.push(markRaw(myText));
                        }
                    }
                }
            }

            if (engravingMeshes.length) {
                if (
                    !this.engravingGroups[model.uuid] ||
                    this.engravingGroups[model.uuid] === undefined
                ) {
                    this.engravingGroups[model.uuid] = markRaw(
                        new THREE.Group()
                    );
                    // Добавляем гравировку как дочерний элемент модели
                    // чтобы она вращалась вместе с моделью
                    model.add(this.engravingGroups[model.uuid]);
                }

                engravingMeshes.forEach((engMesh) => {
                    // Все объекты гравировки уже обернуты в markRaw при добавлении
                    // в массив engravingMeshes, поэтому просто добавляем их в группу
                    this.engravingGroups[model.uuid].add(engMesh);
                });
            }

            return model;
        },

        /**
         * Находит 3D модель по element_id
         */
        findModelByElementId(elementId) {
            // Ищем в elements3D
            for (const slug in this.elements3D) {
                const model = this.elements3D[slug];
                if (model && model.uuid) {
                    // Находим element по meshId
                    const element = this.elements.find(
                        (el) => el.meshId === model.uuid
                    );
                    if (element && element.element_id === elementId) {
                        return model;
                    }
                }
            }
            return null;
        },

        /**
         * Находит 3D модель по meshId
         */
        findModelByMeshId(meshId) {
            let foundModel = null;

            // Ищем в graveGroup
            this.graveGroup.traverse((node) => {
                if (node.uuid === meshId) {
                    foundModel = node;
                }
            });

            if (foundModel) return foundModel;

            // Ищем в elementsGroup
            this.elementsGroup.traverse((node) => {
                if (node.uuid === meshId) {
                    foundModel = node;
                }
            });

            return foundModel;
        },

        /**
         * Обновляет гравировку для конкретного элемента
         */
        async updateEngravingForElement(elementId) {
            const element = this.elements.find(
                (el) => el.element_id === elementId
            );
            if (!element || !element.meshId) {
                return;
            }

            const model = this.findModelByMeshId(element.meshId);

            if (!model) {
                const modelByElementId = this.findModelByElementId(elementId);
                if (modelByElementId) {
                    await this.addEngraving(modelByElementId, element);
                }
                return;
            }

            await this.addEngraving(model, element);
        },

        /**
         * Обновляет все гравировки на сцене
         */
        async updateAllEngravings() {
            if (!this.scene || !this.elements) {
                return;
            }

            for (const element of this.elements) {
                if (element.meshId) {
                    const model = this.findModelByMeshId(element.meshId);
                    if (model) {
                        await this.addEngraving(model, element);
                    }
                }
            }
        },

        /**
         * Обновляет все гравировки на сцене (алиас для совместимости)
         */
        async refreshAllEngravings() {
            return this.updateAllEngravings();
        },

        addPlot(plot) {
            const add1mm = 1 / 1000;

            const plotWidth = parseFloat(plot.width) / 100 + 2 * add1mm;
            const plotLength = parseFloat(plot.length) / 100 + 2 * add1mm;

            let plotMaterial = new THREE.MeshStandardMaterial({
                color: 0xcecece,
            });

            if (parseInt(this.settings.plot_texture) != 1) {
                let plotMaterialTexture = "/textures/plot/zemlya.jpg";

                if (parseInt(this.settings.plot_texture) == 2) {
                    plotMaterialTexture = this.settingImages.plot_texture;
                }
                plotMaterial = getMaterialTexture(
                    plotMaterialTexture,
                    false,
                    true
                );
            }

            this.plot3D = markRaw(
                new THREE.Mesh(
                    new THREE.BoxGeometry(
                        plotWidth,
                        plotLength,
                        this.plotHeight
                    ),
                    plotMaterial
                )
            );

            this.plot3D.rotation.x = -Math.PI * 0.5;
            this.plot3D.receiveShadow = true;
            this.plot3D.position.y = -this.plotHeight / 2;
            this.plot3D.userData.ground = true;
            this.plot3D.name = "plot";

            this.scene.add(this.plot3D);
        },

        async addElement(el) {
            // Проверяем, что компонент инициализирован и не размонтирован
            if (!this.graveGroup || !this.scene) {
                console.warn('ThreeCanvas: компонент не инициализирован или размонтирован');
                return;
            }
            
            let gltf = await getGLTF(el.model3d);

            if (gltf) {
                if (
                    el.type_calc_cost != "fix" &&
                    el.material &&
                    el.material.image
                ) {
                    const materialImage =
                        el.material.imageOriginal || el.material.image;
                    const material = getMaterialTexture(
                        materialImage,
                        true,
                        true
                    );

                    gltf.scene.traverse(function (node) {
                        if (node.isMesh) {
                            if (!node.name.includes("_notexture")) {
                                node.material = material;
                            }
                        }
                    });

                    if (el.element_group_slug == "cvetniki-nadgrobiya") {
                        let cvetnikMaterial = new THREE.MeshStandardMaterial({
                            color: 0xcecece,
                        });

                        if (
                            parseInt(this.settings.filler_cvetnik_texture) != 1
                        ) {
                            let cvetnikMaterialTexture =
                                "/textures/cvetnik/grass.jpg";

                            if (
                                parseInt(
                                    this.settings.filler_cvetnik_texture
                                ) == 2
                            ) {
                                cvetnikMaterialTexture =
                                    this.settingImages.filler_cvetnik_texture;
                            }
                            cvetnikMaterial = getMaterialTexture(
                                cvetnikMaterialTexture,
                                false,
                                true
                            );
                        }

                        gltf.scene.traverse(function (node) {
                            if (node.isMesh) {
                                if (node.name.includes("_grass")) {
                                    node.material = cvetnikMaterial;
                                }
                            }
                        });
                    }
                } else if (el.element_group_slug == "cvetniki-nadgrobiya") {
                    let cvetnikMaterial = new THREE.MeshStandardMaterial({
                        color: 0xcecece,
                    });

                    if (parseInt(this.settings.filler_cvetnik_texture) != 1) {
                        let cvetnikMaterialTexture =
                            "/textures/cvetnik/grass.jpg";

                        if (
                            parseInt(this.settings.filler_cvetnik_texture) == 2
                        ) {
                            cvetnikMaterialTexture =
                                this.settingImages.filler_cvetnik_texture;
                        }
                        cvetnikMaterial = getMaterialTexture(
                            cvetnikMaterialTexture,
                            false,
                            true
                        );
                    }

                    gltf.scene.traverse(function (node) {
                        if (node.isMesh) {
                            if (node.name.includes("_grass")) {
                                node.material = cvetnikMaterial;
                            }
                        }
                    });
                }

                let scaleCoef = { x: 1, y: 1, z: 1 };

                let model = null;

                if (el.size) {
                    if (el.size.model3d) {
                        const sizeGltf = await getGLTF(el.size.model3d);
                        if (sizeGltf) {
                            if (
                                parseInt(el.individual_size_material) == 0 &&
                                el.material &&
                                el.material.image
                            ) {
                                const materialImage =
                                    el.material.imageOriginal ||
                                    el.material.image;
                                const material = getMaterialTexture(
                                    materialImage,
                                    true,
                                    true
                                );

                                sizeGltf.scene.traverse(function (node) {
                                    if (node.isMesh) {
                                        if (!node.name.includes("_notexture")) {
                                            node.material = material;
                                        }
                                    }
                                });
                            }
                            model = markRaw(await modelToMesh(sizeGltf.scene));
                        }
                    } else {
                        if (
                            el.size.width &&
                            el.size.height &&
                            el.size.length &&
                            el.base_size &&
                            el.base_size.width &&
                            el.base_size.height &&
                            el.base_size.length
                        ) {
                            scaleCoef = {
                                x:
                                    parseFloat(el.size.width) /
                                    parseFloat(el.base_size.width),
                                y:
                                    parseFloat(el.size.height) /
                                    parseFloat(el.base_size.height),
                                z:
                                    parseFloat(el.size.length) /
                                    parseFloat(el.base_size.length),
                            };

                            model = markRaw(await modelToMesh(gltf.scene));
                            model.geometry.scale(
                                scaleCoef.x,
                                scaleCoef.y,
                                scaleCoef.z
                            );
                        }
                    }
                }

                // Если model не удалось создать - используем базовую модель gltf
                if (!model && gltf) {
                    model = markRaw(await modelToMesh(gltf.scene));
                }

                // Проверяем что model создан перед использованием
                if (!model) {
                    console.warn(
                        "ThreeCanvas: не удалось создать model для элемента",
                        el.element_id
                    );
                    return;
                }

                this.$store.dispatch("clientOrder/setMeshIdElement", {
                    elementId: el.element_id,
                    meshId: model.uuid,
                });

                if (
                    el.position &&
                    (el.position.x || el.position.y || el.position.z)
                ) {
                    model.position.copy(el.position);
                    model.updateMatrix();
                }

                if (+model.position.y === 0) {
                    model = await modelSetY(model);
                    model.updateMatrix();

                    this.$store.dispatch("clientOrder/setPositionElement", {
                        meshId: model.uuid,
                        position: model.position,
                    });
                }

                //add faces
                model = await this.addEngraving(model, el);

                if (el.rotate) {
                    model.rotation.y = el.rotate;
                    model.updateMatrix();
                }

                // сохраняем mesh без пересоздания объекта-хранилища,
                // чтобы не терять markRaw
                this.elements3D[el.element_group_slug] = model;

                model.nameType = "element";
                model.isCheckup = true;

                // Проверяем что graveGroup существует перед attach
                if (this.graveGroup) {
                    this.graveGroup.attach(model);
                } else {
                    console.warn('ThreeCanvas: graveGroup не инициализирован');
                    return;
                }

                // if (
                //     el.element_group_slug == "stely" ||
                //     el.element_group_slug == "cvetniki-nadgrobiya" ||
                //     el.element_group_slug == "tumby"
                // ) {
                //     this.graveGroup.attach(
                //         this.elements3D[el.element_group_slug]
                //     );
                // } else if (
                //     el.element_group_slug == "vazy" ||
                //     el.element_group_slug == "stoliki" ||
                //     el.element_group_slug == "skameiki"
                // ) {
                //     this.elementsGroup.add(
                //         this.elements3D[el.element_group_slug]
                //     );
                // } else {
                //     this.scene.add(this.elements3D[el.element_group_slug]);
                // }

                this.addMovingObject(model, el);
            }
            this.sceneChanged();
        },
        async addLandscapingMainElement(el) {
            let model = null;
            if (el.element_group_slug == "plate-ready") {
                const gltf = await getGLTF(el.model3d);
                if (el.texture) {
                    const material = getMaterialTexture(el.texture, true, true);

                    gltf.scene.traverse(function (node) {
                        if (node.isMesh) {
                            if (!node.name.includes("_notexture")) {
                                node.material = material;
                            }
                        }
                    });
                }
                model = markRaw(await modelToMesh(gltf.scene));

                if (
                    el.position &&
                    (el.position.x || el.position.y || el.position.z)
                ) {
                    model.position.copy(el.position);
                }

                if (+model.position.y === 0) {
                    model = await modelSetY(model);
                }

                model.nameType = "mainLandscaping";
                model.isCheckup = true;

                this.landscaping3D.mainElement = model;

                this.$store.dispatch(
                    "clientOrder/setMeshIdLandscapingMainElement",
                    {
                        meshId: model.uuid,
                    }
                );
                this.$store.dispatch(
                    "clientOrder/setPositionLandscapingMainElement",
                    {
                        meshId: model.uuid,
                        position: model.position,
                    }
                );

                this.landscapingGroup.add(this.landscaping3D.mainElement);

                if (el.rotate) {
                    model.rotation.y = el.rotate;
                    model.updateMatrix();
                }

                // добавляем в DragControls только после того, как объект добавлен в группу
                this.addMovingObject(model, el);
            } else if (el.element_group_slug == "plate-fill") {
                if (
                    el.size &&
                    el.size.width &&
                    el.size.length &&
                    el.size.height &&
                    el.size.depth
                ) {
                    const plateHeight =
                        (parseFloat(el.size.height) +
                            parseFloat(el.size.depth)) /
                        100;

                    const plateWidth = parseFloat(el.size.width) / 100;
                    const plateDepth = parseFloat(el.size.length) / 100;

                    const plateGeometry = new THREE.BoxGeometry(
                        plateWidth,
                        plateHeight,
                        plateDepth
                    );

                    const plateFill = markRaw(
                        new THREE.Mesh(
                            plateGeometry,
                            getMaterialTexture(el.texture, false, true)
                        )
                    );

                    if (parseInt(el.individual_size_material)) {
                        const elBaseWidth =
                            parseFloat(el.base_size.width) / 100;
                        const elBaseLength =
                            parseFloat(el.base_size.length) / 100;

                        plateFill.position.x +=
                            plateWidth / 2 - elBaseWidth / 2;
                        plateFill.position.z +=
                            plateDepth / 2 - elBaseLength / 2;
                        if (parseFloat(el.vistup_back)) {
                            plateFill.position.z -=
                                parseFloat(el.vistup_back) / 100;
                        }
                        if (parseFloat(el.vistup_left)) {
                            plateFill.position.x -=
                                parseFloat(el.vistup_left) / 100;
                        }
                        if (el.moving != "no") {
                            this.objectsMoving.push(markRaw(plateFill));
                            this.initDragControls();
                        }
                    }

                    this.landscaping3D.mainElement = plateFill;
                    plateFill.nameType = "mainLandscaping";
                    plateFill.isCheckup = true;
                    this.landscapingGroup.add(plateFill);
                }
            } else if (el.element_group_slug == "stripe") {
                if (
                    el.size &&
                    el.size.width &&
                    el.size.length &&
                    el.size.height &&
                    el.size.depth &&
                    el.thickness
                ) {
                    const stripe = await addStripe(
                        el,
                        this.settings,
                        this.settingImages
                    );

                    if (parseInt(el.individual_size_material)) {
                        if (el.position && el.position.z) {
                            stripe.position.z = el.position.z;
                        } else {
                            if (parseFloat(el.vistup_back)) {
                                stripe.position.z =
                                    stripe.position.z -
                                    parseFloat(el.vistup_back) / 100 / 2;
                            }
                            if (parseFloat(el.vistup_front)) {
                                stripe.position.z =
                                    stripe.position.z +
                                    parseFloat(el.vistup_front) / 100 / 2;
                            }
                        }

                        if (el.position && el.position.x) {
                            stripe.position.x = el.position.x;
                        } else {
                            if (parseFloat(el.vistup_left)) {
                                stripe.position.x =
                                    stripe.position.x -
                                    parseFloat(el.vistup_left) / 100 / 2;
                            }

                            if (parseFloat(el.vistup_right)) {
                                stripe.position.x =
                                    stripe.position.x +
                                    parseFloat(el.vistup_right) / 100 / 2;
                            }
                        }

                        this.$store.dispatch(
                            "clientOrder/setMeshIdLandscapingMainElement",
                            {
                                meshId: stripe.uuid,
                            }
                        );
                        this.$store.dispatch(
                            "clientOrder/setPositionLandscapingMainElement",
                            {
                                meshId: stripe.uuid,
                                position: stripe.position,
                            }
                        );
                    }

                    let fillingObject = {
                        width: 0,
                        length: 0,
                        height: 0,
                    };
                    const thicknessM = parseInt(el.thickness) / 100;
                    const mainElementBbox = getPosInfo(stripe);
                    fillingObject.height = mainElementBbox.bbox.max.y;
                    fillingObject.width =
                        mainElementBbox.xSize - thicknessM * 2;
                    fillingObject.length =
                        mainElementBbox.zSize - thicknessM * 2;
                    const fillElement = markRaw(
                        new THREE.Mesh(
                            new THREE.BoxGeometry(
                                fillingObject.width,
                                fillingObject.length,
                                fillingObject.height
                            ),
                            this.plot3D.material
                        )
                    );
                    fillElement.rotation.x = -Math.PI / 2;
                    fillElement.position.copy(stripe.position);
                    fillElement.position.y = fillingObject.height / 2;

                    fillElement.updateMatrix();

                    fillElement.nameType = "fillLandscaping";
                    fillElement.isCheckup = true;
                    this.landscapingGroup.add(fillElement);

                    this.landscaping3D.mainElement = stripe;

                    stripe.nameType = "mainLandscaping";
                    stripe.isCheckup = true;
                    this.landscapingGroup.add(stripe);

                    if (el.rotate) {
                        stripe.rotation.y = el.rotate;
                        stripe.updateMatrix();
                    }

                    // добавляем в DragControls только после того, как объект добавлен в группу
                    this.addMovingObject(stripe, el);
                }
            } else if (
                el.element_group_slug == "pour" ||
                el.element_group_slug == "grass"
            ) {
                //новый способ
                const landWidth = parseFloat(this.plot.width) / 100 - 0.001;
                const landLength = parseFloat(this.plot.length) / 100 - 0.001;

                const land = markRaw(
                    new THREE.Mesh(
                        new THREE.BoxGeometry(
                            landWidth,
                            landLength,
                            this.plotHeight
                        ),
                        getMaterialTexture(el.texture, false, true)
                    )
                );

                land.rotation.x = -Math.PI * 0.5;
                land.receiveShadow = true;
                land.position.y += this.plotHeight / 2;
                //новый способ

                land.geometry.setAttribute(
                    "uv2",
                    new THREE.Float32BufferAttribute(
                        land.geometry.attributes.uv.array,
                        2
                    )
                );

                this.landscaping3D.mainElement = land;

                land.nameType = "mainLandscaping";
                land.isCheckup = true;

                this.landscapingGroup.add(land);
            } else if (el.element_group_slug == "paving") {
                const plot = getPosInfo(this.plot3D);

                if (plot) {
                    const gltf = await getGLTF(el.model3d);
                    const initialPlitka = gltf.scene;
                    let material = null;

                    if (el.texture) {
                        material = getMaterialTexture(el.texture, true);

                        initialPlitka.traverse(function (node) {
                            if (node.isMesh) {
                                if (!node.name.includes("_notexture")) {
                                    node.material = material;
                                }
                            }
                        });
                    }

                    const paving = await addPaving(
                        initialPlitka,
                        plot.xSize,
                        plot.zSize,
                        plot.bbox.min.x,
                        plot.bbox.min.z,
                        plot.bbox.max.y,
                        material
                    );

                    this.landscaping3D.mainElement = markRaw(paving);

                    paving.nameType = "mainLandscaping";
                    paving.isCheckup = true;

                    this.landscapingGroup.add(paving);
                }
            }
            this.sceneChanged();
        },
        async addFacingElement(el, place) {
            if (
                !el ||
                !this.landscaping3D ||
                !this.landscaping3D.mainElement ||
                !this.landscaping ||
                !this.landscaping.mainElement ||
                !this.landscaping.mainElementType
            ) {
                return;
            }
            let initialPlitka = null;

            if (el?.model3d) {
                try {
                    const gltf = await getGLTF(el.model3d);
                    initialPlitka = gltf.scene;
                } catch (e) {
                    initialPlitka = null;
                }
            }

            if (!initialPlitka) {
                const widthCm = Number(el?.size?.width ?? el?.base_size?.width);
                const lengthCm = Number(
                    el?.size?.length ?? el?.base_size?.length
                );
                const depthCm = Number(el?.size?.depth ?? el?.base_size?.depth);
                const heightCm = Number(
                    el?.size?.height ?? el?.base_size?.height
                );

                const widthM =
                    Number.isFinite(widthCm) && widthCm > 0
                        ? widthCm / 100
                        : 0.3;
                const lengthM =
                    Number.isFinite(lengthCm) && lengthCm > 0
                        ? lengthCm / 100
                        : 0.3;

                const thicknessSourceCm =
                    Number.isFinite(depthCm) && depthCm > 0
                        ? depthCm
                        : Number.isFinite(heightCm) && heightCm > 0
                        ? Math.min(heightCm, 3)
                        : 1;
                const thicknessM = Math.max(thicknessSourceCm / 100, 0.005);

                initialPlitka = markRaw(
                    new THREE.Mesh(
                        new THREE.BoxGeometry(widthM, thicknessM, lengthM),
                        new THREE.MeshStandardMaterial({ color: 0xffffff })
                    )
                );
            }

            if (el.texture) {
                const material = getMaterialTexture(el.texture, true);
                initialPlitka.traverse(function (node) {
                    if (node.isMesh) {
                        node.material = material;
                    }
                });
                initialPlitka.updateMatrix();
            }

            const elementBbox = getPosInfo(this.landscaping3D.mainElement);

            const typeElement = this.landscaping.mainElementType.slug;
            const heightOnEarth =
                parseFloat(this.landscaping.mainElement.size.height) / 100;

            if (
                place == "top" &&
                (typeElement == "plate-fill" || typeElement == "stripe")
            ) {
                try {
                    let facing_top;

                    if (typeElement == "stripe") {
                        facing_top = await addFacingTopStripe(
                            initialPlitka,
                            this.landscaping3D.mainElement,
                            this.landscaping.mainElement
                        );
                    } else {
                        facing_top = await addFacingTop(
                            initialPlitka,
                            elementBbox,
                            heightOnEarth
                        );
                    }

                    const bboxMainElement = getPosInfo(
                        this.landscaping3D.mainElement
                    );
                    const bboxFacingElement = getPosInfo(facing_top);

                    const desiredMinY = bboxMainElement.bbox.max.y + 0.00001;
                    const deltaY = desiredMinY - bboxFacingElement.bbox.min.y;
                    facing_top.position.y += deltaY;
                    facing_top.updateMatrix();

                    this.landscaping3D.facing_top = markRaw(facing_top);

                    facing_top.isCheckup = true;
                    facing_top.nameType = "facingTopLandscaping";

                    this.landscapingGroup.add(this.landscaping3D.facing_top);
                } catch (e) {
                    return;
                }
            }

            if (
                place == "side" &&
                (typeElement == "plate-fill" || typeElement == "stripe")
            ) {
                try {
                    const heightTopY =
                        elementBbox?.bbox?.max?.y ?? heightOnEarth;
                    let facing_side = addFacingSide(
                        initialPlitka,
                        elementBbox,
                        heightTopY
                    );

                    const bboxFacingSide = getPosInfo(facing_side);
                    const desiredMaxY = heightTopY - 0.00001;
                    const deltaY = desiredMaxY - bboxFacingSide.bbox.max.y;
                    facing_side.position.y += deltaY;
                    facing_side.updateMatrix();

                    this.landscaping3D.facing_side = markRaw(facing_side);

                    this.landscapingGroup.add(this.landscaping3D.facing_side);
                } catch (e) {
                    return;
                }
            }
        },
        async addLandscapingFillElement(el) {
            if (
                el.element_group_slug == "pour" ||
                el.element_group_slug == "grass"
            ) {
                // //новый способ
                // const landWidth =
                //     parseFloat(this.landscaping.mainElement.size.width) / 100 -
                //     parseFloat(this.landscaping.mainElement.thickness) / 100;
                // const landLength =
                //     parseFloat(this.landscaping.mainElement.size.length) / 100 -
                //     parseFloat(this.landscaping.mainElement.thickness) / 100;

                let fillingObject = {
                    width: 0,
                    length: 0,
                };

                const thicknessM =
                    parseInt(this.landscaping.mainElement.thickness) / 100;

                const mainEl = getPosInfo(this.landscaping3D["mainElement"]);

                if (
                    this.landscaping.mainElement.element_group_slug == "stripe"
                ) {
                    fillingObject.width = mainEl.xSize - thicknessM * 2 - 0.001;
                    fillingObject.length =
                        mainEl.zSize - thicknessM * 2 - 0.001;
                }

                const land = markRaw(
                    new THREE.Mesh(
                        new THREE.BoxBufferGeometry(
                            fillingObject.width,
                            fillingObject.length,
                            this.plotHeight
                        ),
                        getMaterialTexture(el.texture, false, true)
                    )
                );

                land.rotation.x = -Math.PI * 0.5;
                //новый способ

                land.receiveShadow = true;
                land.geometry.setAttribute(
                    "uv2",
                    new THREE.Float32BufferAttribute(
                        land.geometry.attributes.uv.array,
                        2
                    )
                );
                land.updateMatrix();

                const bboxMainElement = getPosInfo(
                    this.landscaping3D.mainElement
                );

                const bboxFillElement = getPosInfo(land);

                land.position.y =
                    bboxMainElement.bbox.max.y - bboxFillElement.ySize / 2;
                land.updateMatrix();

                this.landscaping3D.fillElement = land;

                land.nameType = "fillLandscaping";
                land.isCheckup = true;

                this.landscapingGroup.add(land);
            } else if (el.element_group_slug == "paving") {
                let fillingObject = {
                    width: 0,
                    length: 0,
                    xMin: 0,
                    xMax: 0,
                    zMin: 0,
                    zMax: 0,
                };

                const thicknessM =
                    parseInt(this.landscaping.mainElement.thickness) / 100;

                const mainEl = getPosInfo(this.landscaping3D["mainElement"]);

                if (
                    this.landscaping.mainElement.element_group_slug == "stripe"
                ) {
                    fillingObject.width = mainEl.xSize - thicknessM * 2 - 0.001;
                    fillingObject.length =
                        mainEl.zSize - thicknessM * 2 - 0.001;
                    fillingObject.xMin = mainEl.bbox.min.x + thicknessM;
                    fillingObject.xMax = mainEl.bbox.max.x - thicknessM;
                    fillingObject.zMin = mainEl.bbox.min.z + thicknessM;
                    fillingObject.zMax = mainEl.bbox.max.z - thicknessM;
                }
                if (fillingObject) {
                    const gltf = await getGLTF(el.model3d);
                    const initialPlitka = gltf.scene;
                    let material = null;

                    if (el.texture) {
                        material = getMaterialTexture(el.texture, true);
                        initialPlitka.traverse(function (node) {
                            if (node.isMesh) {
                                node.material = material;
                            }
                        });
                        initialPlitka.updateMatrix();
                    }

                    const fillElement = await addPaving(
                        initialPlitka,
                        fillingObject.width,
                        fillingObject.length,
                        fillingObject.xMin,
                        fillingObject.zMin,
                        0,
                        material
                    );

                    const bboxMainElement = getPosInfo(
                        this.landscaping3D.mainElement
                    );
                    const bboxFillElement = getPosInfo(fillElement);

                    fillElement.position.y =
                        bboxMainElement.bbox.max.y - bboxFillElement.ySize;
                    fillElement.updateMatrix();

                    this.landscaping3D.fillElement = markRaw(fillElement);

                    fillElement.nameType = "fillLandscaping";
                    fillElement.isCheckup = true;

                    this.landscapingGroup.add(fillElement);
                }
            }
            this.sceneChanged();
        },
        async addLandscapingBorderElement(el) {
            const plot = getPosInfo(this.plot3D);

            if (plot) {
                const gltf = await getGLTF(el.model3d);
                const initialBorder = gltf.scene;

                if (el.texture) {
                    const material = getMaterialTexture(el.texture, true);
                    initialBorder.traverse(function (node) {
                        if (node.isMesh) {
                            node.material = material;
                        }
                    });
                }

                const meshInitialBorder = await modelToMesh(initialBorder);

                const bboxInitialBorder = getPosInfo(meshInitialBorder);

                const borderElement = await addBorder(meshInitialBorder, plot);

                if (+el.base_size.depth) {
                    borderElement.position.y =
                        bboxInitialBorder.ySize / 2 - +el.base_size.depth / 100;
                    borderElement.updateMatrix();
                }

                //дополнительно поднять заполнитель
                const bboxBorderElement = getPosInfo(borderElement);
                const bboxMainElementLandscaping = getPosInfo(
                    this.landscaping3D.mainElement
                );

                if (
                    bboxBorderElement.bbox.max.y >
                    bboxMainElementLandscaping.bbox.max.y
                ) {
                    this.landscaping3D.mainElement.position.y =
                        bboxBorderElement.bbox.max.y -
                        bboxMainElementLandscaping.ySize;
                    this.landscaping3D.mainElement.updateMatrix();
                    const pseudoElementHeight =
                        this.landscaping3D.mainElement.position.y -
                        bboxMainElementLandscaping.ySize / 2;
                    const pseudoElement = new THREE.Mesh(
                        new THREE.BoxGeometry(
                            plot.xSize,
                            pseudoElementHeight,
                            plot.zSize
                        ),
                        this.plot3D.material
                    );
                    pseudoElement.position.y = pseudoElementHeight / 2;
                    pseudoElement.updateMatrix();
                    this.landscapingGroup.add(pseudoElement);
                }

                this.landscaping3D.borderElement = markRaw(borderElement);

                borderElement.nameType = "borderLandscaping";
                borderElement.isCheckup = true;

                this.landscapingGroup.add(borderElement);
            }
            this.sceneChanged();
        },

        //add Barrier
        async addBarrierElement(el) {
            //в plot добавляем либо участок либо группу с бордюром
            const plot = getPosInfo(this.plot3D);

            if (plot) {
                let polotno = null;
                let stolbik = null;
                let kalitka = null;

                let gltf = await getGLTF(el.model3dCanvas);
                if (gltf) {
                    polotno = await modelToMesh(gltf.scene);
                }
                gltf = await getGLTF(el.model3dColumn);
                if (gltf) {
                    stolbik = await modelToMesh(gltf.scene);
                }
                gltf = await getGLTF(el.model3dGate);
                if (gltf) {
                    kalitka = await modelToMesh(gltf.scene);
                }

                this.barrier3D = markRaw(
                    await addBarrier(polotno, stolbik, kalitka, plot, el)
                );

                this.$store.dispatch("clientOrder/setMeshIdElement", {
                    elementId: el.element_id,
                    meshId: this.barrier3D.uuid,
                });

                this.barrier3D.nameType = "barrier";
                this.barrier3D.isCheckup = true;

                this.barrierGroup.add(this.barrier3D);
            }
            this.sceneChanged();
        },

        addMovingObject(model, el) {
            //здесь проверять движущийся/вращающийся/подпрыгивающий
            if (
                (el.moving == "move" || el.moving == "lift") &&
                model &&
                model.isObject3D
            ) {
                this.$store.dispatch(
                    "Client3dInteractive/addMovingUuid",
                    model.uuid
                );
                this.objectsMoving.push(markRaw(model));
            }
        },

        /**
         * Освобождает все ресурсы Three.js для предотвращения утечек памяти
         */
        disposeResources() {
            // Останавливаем анимационный цикл
            if (this.animationFrameId !== null) {
                cancelAnimationFrame(this.animationFrameId);
                this.animationFrameId = null;
            }

            // Удаляем event listeners с canvas
            if (this.canvas) {
                this.canvas.removeEventListener("dblclick", this.doubleClick);
            }

            // Удаляем event listeners со стрелок вращения
            if (this.arrowRotateGroup && this.arrowRotateGroup.children) {
                this.arrowRotateGroup.children.forEach((arrow) => {
                    arrow.removeEventListener(
                        "mouseover",
                        this.handleMouseOver
                    );
                    arrow.removeEventListener("mouseout", this.handleMouseOut);
                    arrow.removeEventListener(
                        "mousedown",
                        this.handleLeftArrowMousedown
                    );
                    arrow.removeEventListener(
                        "mousedown",
                        this.handleRightArrowMousedown
                    );
                });
            }

            // Удаляем event listener со сцены
            if (this.scene && this.sceneMousedownHandler) {
                this.scene.removeEventListener(
                    "mousedown",
                    this.sceneMousedownHandler
                );
                this.sceneMousedownHandler = null;
            }

            // Освобождаем DragControls
            this.disposeDragControls();

            // Очищаем InteractionManager
            if (this.interactionManager) {
                // Удаляем все объекты из InteractionManager
                if (this.scene) {
                    this.interactionManager.remove(this.scene);
                }
                if (this.arrowRotateGroup && this.arrowRotateGroup.children) {
                    this.arrowRotateGroup.children.forEach((element) => {
                        this.interactionManager.remove(element);
                    });
                }
                // InteractionManager не имеет метода dispose, просто очищаем ссылку
                this.interactionManager = null;
            }

            // Вспомогательная функция для рекурсивного dispose объектов сцены
            const disposeObject = (object) => {
                if (!object) return;

                // Очищаем дочерние объекты
                if (object.children && object.children.length > 0) {
                    const children = [...object.children]; // Копируем массив, т.к. он будет изменяться
                    children.forEach((child) => {
                        disposeObject(child);
                        object.remove(child);
                    });
                }

                // Освобождаем геометрию
                if (
                    object.geometry &&
                    typeof object.geometry.dispose === "function"
                ) {
                    object.geometry.dispose();
                }

                // Освобождаем материалы
                if (object.material) {
                    if (Array.isArray(object.material)) {
                        object.material.forEach((material) => {
                            disposeMaterial(material);
                        });
                    } else {
                        disposeMaterial(object.material);
                    }
                }

                // Удаляем event listeners с объекта (Three.js использует EventDispatcher)
                // Event listeners обычно хранятся во внутреннем объекте _listeners
                // Но лучше полагаться на стандартные методы removeEventListener
                // если они были добавлены через addEventListener
            };

            // Вспомогательная функция для dispose материалов
            const disposeMaterial = (material) => {
                if (!material) return;

                // Освобождаем текстуры материала
                Object.keys(material).forEach((key) => {
                    const value = material[key];
                    if (
                        value &&
                        value.isTexture &&
                        typeof value.dispose === "function"
                    ) {
                        value.dispose();
                    }
                    // Обрабатываем массивы текстур (например, в CubeTexture)
                    if (Array.isArray(value)) {
                        value.forEach((item) => {
                            if (
                                item &&
                                item.isTexture &&
                                typeof item.dispose === "function"
                            ) {
                                item.dispose();
                            }
                        });
                    }
                });

                if (typeof material.dispose === "function") {
                    material.dispose();
                }
            };

            // Очищаем все группы и объекты из сцены
            if (this.scene) {
                // Очищаем все engravingGroups с полным освобождением ресурсов
                Object.keys(this.engravingGroups).forEach((uuid) => {
                    this.removeEngraving(uuid); // Используем улучшенный метод
                });
                this.engravingGroups = markRaw({});

                // Очищаем основные группы
                const groups = [
                    this.elementsGroup,
                    this.landscapingGroup,
                    this.borderGroup,
                    this.graveGroup,
                    this.barrierGroup,
                    this.arrowRotateGroup,
                    this.outlineGroup,
                    this.engravingGroup,
                ];

                groups.forEach((group) => {
                    if (group) {
                        disposeObject(group);
                        if (group.parent) {
                            group.parent.remove(group);
                        }
                    }
                });

                // Очищаем все объекты сцены рекурсивно
                // Собираем все объекты, кроме самой сцены
                const objectsToRemove = [];
                this.scene.traverse((object) => {
                    if (object !== this.scene) {
                        objectsToRemove.push(object);
                    }
                });

                // Удаляем объекты (начиная с листьев дерева)
                objectsToRemove.reverse().forEach((object) => {
                    if (object.parent) {
                        disposeObject(object);
                        object.parent.remove(object);
                    }
                });

                // Очищаем окружение и фон
                if (
                    this.environmentMap &&
                    typeof this.environmentMap.dispose === "function"
                ) {
                    this.environmentMap.dispose();
                    this.environmentMap = null;
                }
                this.scene.background = null;
                this.scene.environment = null;

                // Очищаем источники света
                const lights = [
                    this.ambientLight,
                    this.directionalLight,
                    this.fillLight,
                    this.pointLight,
                ];
                lights.forEach((light) => {
                    if (light) {
                        if (light.parent) {
                            light.parent.remove(light);
                        }
                        // Освобождаем shadow map если есть
                        if (
                            light.shadow &&
                            light.shadow.map &&
                            typeof light.shadow.map.dispose === "function"
                        ) {
                            light.shadow.map.dispose();
                        }
                    }
                });

                // Удаляем камеру из сцены
                if (this.camera && this.camera.parent) {
                    this.camera.parent.remove(this.camera);
                }
            }

            // Освобождаем контроллеры
            if (this.controls) {
                if (typeof this.controls.dispose === "function") {
                    this.controls.dispose();
                }
                this.controls = null;
            }

            // Освобождаем рендерер
            if (this.renderer) {
                // Удаляем DOM элемент
                if (
                    this.renderer.domElement &&
                    this.renderer.domElement.parentNode
                ) {
                    this.renderer.domElement.parentNode.removeChild(
                        this.renderer.domElement
                    );
                }
                // Освобождаем контекст WebGL
                if (typeof this.renderer.dispose === "function") {
                    this.renderer.dispose();
                }
                // Очищаем все текстуры из кэша рендерера
                if (this.renderer.info && this.renderer.info.memory) {
                    this.renderer.info.memory.textures = 0;
                    this.renderer.info.memory.geometries = 0;
                    this.renderer.info.memory.programs = 0;
                }
                this.renderer = null;
            }

            // Очищаем загрузчики
            this.cubeTextureLoader = null;
            this.textureLoader = null;

            // Очищаем ссылки на объекты
            this.scene = null;
            this.camera = null;
            this.raycaster = null;
            this.mousePosition = null;
            this.plot3D = null;
            this.barrier3D = null;
            this.elements3D = markRaw({});
            this.landscaping3D = markRaw({});
            this.objectsMoving = [];
            this.activable = null;
            this.canvas = null;

            // Очищаем группы
            this.elementsGroup = null;
            this.landscapingGroup = null;
            this.borderGroup = null;
            this.graveGroup = null;
            this.barrierGroup = null;
            this.arrowRotateGroup = null;
            this.outlineGroup = null;
            this.engravingGroup = null;
        },
    },

    async mounted() {
        this.init();
        this.animate();

        // Сохраняем ссылку на обработчик для последующего удаления
        this.sceneMousedownHandler = (event) => {
            this.clearActivable();
        };
        this.scene.addEventListener("mousedown", this.sceneMousedownHandler);

        if (this.plot) {
            this.addPlot(this.plot);
        }
        if (this.elements) {
            for (const element of this.elements) {
                await this.addElement(element);
            }
        }
        if (this.landscaping) {
            if (this.landscaping.mainElement) {
                await this.addLandscapingMainElement(
                    this.landscaping.mainElement
                );
            }

            if (this.landscaping.fillElement) {
                await this.addLandscapingFillElement(
                    this.landscaping.fillElement
                );
            }

            if (this.landscaping.borderElement) {
                await this.addLandscapingBorderElement(
                    this.landscaping.borderElement
                );
            }

            if (this.landscaping.facing_top) {
                await this.addFacingElement(this.landscaping.facing_top, "top");
            }
            if (this.landscaping.facing_side) {
                await this.addFacingElement(
                    this.landscaping.facing_side,
                    "side"
                );
            }
        }
        if (this.barrier) {
            await this.addBarrierElement(this.barrier);
        }

        // Инициализируем DragControls после загрузки всех элементов
        if (this.objectsMoving.length > 0) {
            await this.initDragControls();
        }
        // this.updatePositions();
        // this.updateAllMaterials();
    },
    computed: {
        settings() {
            return this.$store.state.settings.settings;
        },
        noShowSceneEnv() {
            return this.$store.state.noShowSceneEnv;
        },
        lowPerformance() {
            return this.$store.state.lowPerformance;
        },
        settingImages() {
            return this.$store.state.settings.settingImages;
        },
        plot() {
            return this.$store.state.clientOrder.plot;
        },
        elements() {
            return this.$store.state.clientOrder.elements;
        },
        total() {
            return this.$store.state.clientOrder.total;
        },
        barrier() {
            return this.$store.state.clientOrder.barrier;
        },
        faces() {
            return this.$store.state.clientOrder.faces;
        },
        engravingsUpdatedAt() {
            return this.$store.state.clientOrder.engravingsUpdatedAt;
        },
        landscaping() {
            return this.$store.state.clientOrder.landscaping;
        },
        movingUuids() {
            return this.$store.state.Client3dInteractive.movingUuids;
        },
        rotateAngle() {
            const raw =
                this.settings?.rotate_angle ?? this.settings?.rotateAngle ?? 1;
            const degrees = Number(raw);
            return getAngleNumber(
                Number.isFinite(degrees) && degrees > 0 ? degrees : 1
            );
        },
    },
    watch: {
        plot(plot) {
            if (!this.scene) return;
            
            this.scene.remove(this.plot3D);
            this.addPlot(plot);
            this.sceneChanged();
        },
        async elements(elements) {
            // Проверяем инициализацию компонента
            if (!this.graveGroup || !this.elementsGroup || !this.scene) {
                console.warn('ThreeCanvas: компонент не инициализирован, пропускаем обновление elements');
                return;
            }
            
            // Полностью пересобираем надгробные элементы и drag‑контролы
            this.graveGroup.clear();
            this.elementsGroup.clear();

            // Старые ссылки на меши больше неактуальны
            this.objectsMoving = [];
            this.disposeDragControls();

            // сбрасываем хранилище мешей как markRaw‑объект,
            // чтобы Vue не оборачивал three.js‑объекты в Proxy
            this.elements3D = markRaw({});
            this.sceneChanged();

            for (const element of elements) {
                await this.addElement(element);
            }

            //   this.updatePositions();
            //   this.updateAllMaterials();
            this.clearActivable();

            // после асинхронной дозагрузки элементов гарантированно
            // переинициализируем DragControls, чтобы новые объекты
            // сразу стали интерактивными без перезагрузки страницы
            if (this.objectsMoving.length > 0) {
                await this.initDragControls();
            }
        },
        async landscaping(landscaping) {
            // Проверяем инициализацию компонента
            if (!this.landscapingGroup || !this.scene) {
                console.warn('ThreeCanvas: компонент не инициализирован, пропускаем обновление landscaping');
                return;
            }
            
            this.landscapingGroup.clear();

            // Сбрасываем движущиеся элементы благоустройства
            this.objectsMoving = this.objectsMoving.filter(
                (obj) =>
                    obj &&
                    obj.nameType !== "mainLandscaping" &&
                    obj.nameType !== "fillLandscaping" &&
                    obj.nameType !== "borderLandscaping"
            );
            this.disposeDragControls();

            // сохраняем объект с геометрией как markRaw,
            // иначе Vue‑Proxy может ломать внутренние поля three.js
            this.landscaping3D = markRaw({});

            this.sceneChanged();

            if (landscaping.mainElement) {
                await this.addLandscapingMainElement(landscaping.mainElement);
            }

            if (landscaping.fillElement) {
                await this.addLandscapingFillElement(landscaping.fillElement);
            }

            if (landscaping.borderElement) {
                await this.addLandscapingBorderElement(
                    landscaping.borderElement
                );
            }

            if (landscaping.facing_top) {
                await this.addFacingElement(landscaping.facing_top, "top");
            }
            if (landscaping.facing_side) {
                await this.addFacingElement(landscaping.facing_side, "side");
            }

            //   this.updatePositions();
            //   this.updateAllMaterials();
            this.clearActivable();

            // чтобы новые объекты благоустройства сразу можно было
            // перетаскивать, дополнительно переинициализируем DragControls
            if (this.objectsMoving.length > 0) {
                await this.initDragControls();
            }
        },
        async barrier(barrier) {
            // Проверяем инициализацию компонента
            if (!this.barrierGroup || !this.scene) {
                console.warn('ThreeCanvas: компонент не инициализирован, пропускаем обновление barrier');
                return;
            }
            
            this.barrierGroup.clear();

            // Удаляем из списка движущихся только элементы‑ограды
            this.objectsMoving = this.objectsMoving.filter(
                (obj) => obj && obj.nameType !== "barrier"
            );
            this.disposeDragControls();

            this.barrier3D = null;
            this.sceneChanged();

            if (barrier) {
                await this.addBarrierElement(barrier);
            }

            //   this.updatePositions();
            //   this.updateAllMaterials();
            this.clearActivable();

            // аналогично для оград — делаем их интерактивными сразу
            if (this.objectsMoving.length > 0) {
                await this.initDragControls();
            }
        },
        movingUuids(uuids) {
            // Оставляем в списке только те объекты, которые действительно
            // могут двигаться в текущем режиме интерактива.
            this.objectsMoving = this.objectsMoving.filter(
                (obj) => obj && uuids.includes(obj.uuid)
            );

            // Раньше здесь удалялась гравировка для всех объектов,
            // UUID которых отсутствует в списке перемещаемых.
            // В результате гравировка на «неподвижных» фигурах
            // пропадала сразу после обновления movingUuids.
            // Теперь гравировку не трогаем — она должна оставаться
            // видимой вне зависимости от того, можно ли элемент двигать.

            if (this.activable && !uuids.includes(this.activable.uuid)) {
                this.removeArrowRotate();
                this.removeOutline();
            }

            // Используем общий метод initDragControls вместо дублирования кода
            this.initDragControls();
        },

        objectsMoving(objsM) {
            // Используем общий метод initDragControls вместо дублирования кода
            this.initDragControls();
        },

        /**
         * Обновляет гравировки при изменении данных faces в store
         * Вызывается после редактирования гравировки в Engraving.vue
         */
        faces: {
            async handler(newFaces, oldFaces) {
                if (!oldFaces || !oldFaces.length || !this.scene) {
                    return;
                }

                const changedElementIds = new Set();

                for (const newFace of newFaces) {
                    const oldFace = oldFaces.find(
                        (f) => f.face_id === newFace.face_id
                    );

                    if (!oldFace) {
                        changedElementIds.add(newFace.element_id);
                        continue;
                    }

                    const normalizeFaceElements = (elements) =>
                        (elements || []).map((el) => ({
                            element_id: el?.element_id ?? null,
                            width: el?.width ?? null,
                            height: el?.height ?? null,
                            image: el?.image ?? null,
                            imageOriginal: el?.imageOriginal ?? null,
                            rotate: el?.rotate ?? null,
                            position: el?.position
                                ? {
                                      x: el.position.x ?? null,
                                      y: el.position.y ?? null,
                                      z: el.position.z ?? null,
                                  }
                                : null,
                        }));

                    const normalizeFaceTexts = (texts) =>
                        (texts || []).map((t) => ({
                            text: t?.text ?? null,
                            font: t?.font ?? null,
                            size: t?.size ?? null,
                            color: t?.color ?? null,
                            align: t?.align ?? null,
                            position: t?.position
                                ? {
                                      x: t.position.x ?? null,
                                      y: t.position.y ?? null,
                                      z: t.position.z ?? null,
                                  }
                                : null,
                        }));

                    const elementsChanged =
                        newFace.elements.length !== oldFace.elements.length ||
                        JSON.stringify(normalizeFaceElements(newFace.elements)) !==
                            JSON.stringify(
                                normalizeFaceElements(oldFace.elements)
                            );

                    const textsChanged =
                        newFace.texts.length !== oldFace.texts.length ||
                        JSON.stringify(normalizeFaceTexts(newFace.texts)) !==
                            JSON.stringify(normalizeFaceTexts(oldFace.texts));

                    if (elementsChanged || textsChanged) {
                        changedElementIds.add(newFace.element_id);
                    }
                }

                for (const elementId of changedElementIds) {
                    await this.updateEngravingForElement(elementId);
                }

                if (this.objectsMoving.length > 0) {
                    await this.initDragControls();
                }
            },
            deep: true,
        },

        /**
         * Отслеживает изменения в гравировках через timestamp
         */
        async engravingsUpdatedAt(newVal, oldVal) {
            if (!oldVal || !this.scene) {
                return;
            }

            await this.refreshAllEngravings();

            if (this.objectsMoving.length > 0) {
                await this.initDragControls();
            }
        },
    },
    beforeUnmount() {
        // Освобождаем все ресурсы Three.js перед размонтированием компонента
        this.disposeResources();
    },
};
</script>
